import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../../careand-redux/store';

const HOURS = 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 1 * HOURS;
const MIN_CHECK_GAP_MS = 0.5 * HOURS;

// Only meaningful against a deployed bucket: locally there is no version.json
// and __APP_VERSION__ is 'dev'
const ENABLED = import.meta.env.PROD && __APP_VERSION__ !== 'dev';

/**
 * checks whether there's a new version of the app deployed
 */
const useCheckNewVersionAvailable = () => {
  const oldVersion = useAppSelector((state) => state.ui.oldVersion);
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const lastCheck = useRef(0);

  useEffect(() => {
    if (!ENABLED) return undefined;

    let cancelled = false;
    const check = async () => {
      const now = Date.now();
      if (now - lastCheck.current < MIN_CHECK_GAP_MS) return;
      lastCheck.current = now;
      try {
        const res = await fetch(`/version.json?ts=${now}`, { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        if (!cancelled && version && version !== __APP_VERSION__) {
          setNewVersionAvailable(true);
        }
      } catch {
        // offline or transient network error, retry on next tick
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };

    // A hashed chunk from this build failed to load: it was most likely
    // replaced by a newer deploy, so re-surface the banner even if dismissed
    const onPreloadError = () => {
      setNewVersionAvailable(true);
    };

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('vite:preloadError', onPreloadError);
    check();

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('vite:preloadError', onPreloadError);
    };
  }, []);

  return !oldVersion && newVersionAvailable;
};

export default useCheckNewVersionAvailable;
